
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { ActivatedRoute, Router } from "@angular/router";
import { GlobalErrorHandlerService } from '@k12/admin-services';
import { TasksService } from '../tasks.service';
import { TaskTypeService } from '../task-types.service';
import { ToastMessageService } from '@k12/notifications';
import { ToastMessageType, Task, TaskType, SubTask, TaskTypeLink } from '@k12/models';
import { LoadingSpinnerService } from '@k12/loading';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TaskHeaderInfoComponent } from '@k12/task-view';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-task-link',
  imports: [CommonModule, MatButtonModule, TaskHeaderInfoComponent],
  standalone: true,
  styleUrls: ['./task-link.component.scss'],
  templateUrl: './task-link.component.html'
})
export class TaskLinkComponent implements OnInit, OnDestroy {
  private readonly globalErrorHandlerService: GlobalErrorHandlerService = inject(GlobalErrorHandlerService);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly taskService: TasksService = inject(TasksService);
  private readonly taskTypeService: TaskTypeService = inject(TaskTypeService);
  private readonly toastService = inject(ToastMessageService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly unsubscribe = new Subject<void>();

  public errorMessage: string = '';
  public task: Task | null;
  public taskType: TaskType;
  public taskAction: SubTask | undefined;
  public taskTypeAction: TaskTypeLink | undefined;
  public linkAccessed: boolean = false;
  public recentTaskNote: string = '';
  constructor() {}

  public get canSubmit(): boolean {
    return this.linkAccessed && this.subTaskEditable();
  }

  /**
   * Returns the resolved page URL for the task link, appending task context parameters if necessary.
   * @returns The resolved page URL with task context parameters if applicable.
   */
  public get resolvedPageUrl(): string {
    const actionUri = this.task?.subTasks?.[0]?.actionUri;
    const selectedPage = this.taskTypeAction?.selectedPage ?? '';
    const checkedActionUri = this.hasTemplatePlaceholder(selectedPage) ? actionUri : this.taskTypeAction?.selectedPage;
    return this.appendTaskContext(this.resolvePageUrl(checkedActionUri));
  }

  /**
   *  Checks if the provided value contains a template placeholder in the form of {placeholder}.
   *  @param value The string to check for a template placeholder.
   *  @returns True if the value contains a template placeholder; otherwise, false.
   */
  private hasTemplatePlaceholder(value: string): boolean {
    const openBraceIndex = value.indexOf('{');
    if (openBraceIndex < 0) {
      return false;
    }

    return value.indexOf('}', openBraceIndex + 1) > openBraceIndex + 1;
  }

  /**
   * ActionUri lives on the task TYPE, so it is one string shared by every assigned instance and can
   * never carry the per-instance task/subtask ids. Enrollment program deep links (identified by an
   * existing programToken param) get them appended here so the API can resolve the subject student
   * and the academic year from the task itself - neither travels in the URL, so a tampered link
   * cannot redirect the enrollment at someone else's student.
   *
   * Left untouched for every other link target (internal pages like /mypage, arbitrary external
   * URLs), which have no use for task context.
   */
  private appendTaskContext(url: string): string {
    const taskId = this.task?.id;
    const subTaskId = this.taskAction?.id;
    if (!url || !taskId || !subTaskId) {
      return url;
    }

    // Relative paths are valid here, so this stays string-based rather than using the URL parser.
    const queryStart = url.indexOf('?');
    if (queryStart < 0) {
      return url;
    }

    const query = url.slice(queryStart + 1);
    const isEnrollmentLink = /(^|&)programToken=/.test(query);
    const alreadyCarriesContext = /(^|&)taskId=/.test(query);

    if (!isEnrollmentLink || alreadyCarriesContext ) {
      return url;
    }

    return `${url}&taskId=${encodeURIComponent(taskId)}&subTaskId=${encodeURIComponent(subTaskId)}`;
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    const taskId = this.route.snapshot.paramMap.get('id');
    const taskActionId = this.route.snapshot.paramMap.get('typeId');

    if (!taskId || !taskActionId) {
      this.toastService.showToast(ToastMessageType.Error, 'Invalid task or task action ID.');
      this.globalErrorHandlerService.handleError({ message: 'Invalid task or task action ID.' });
      return;
    }

    this.loadingSpinnerService.showSpinner();
    this.taskService
      .get(taskId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (task) => {
          this.task = task.body;
          if (!this.task?.subTasks) {
            this.toastService.showToast(ToastMessageType.Error, 'Task has no subtasks.');
            this.globalErrorHandlerService.handleError({ message: 'Task has no subtasks.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }
          this.taskAction = this.task?.subTasks?.find(
            (subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase()
          );
          if (!this.subTaskEditable()) {
            this.linkAccessed = true;
          }
          if (!this.taskAction) {
            this.toastService.showToast(ToastMessageType.Error, 'Task action not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task action not found.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }

          this.taskTypeService
            .get(this.taskAction.taskTypeId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (taskType) => {
                if (!taskType.body) {
                  return;
                }
                this.taskType = taskType.body;
                this.taskTypeAction = this.taskType.subTasks?.find(
                  (subTask) => subTask.actionId?.toLowerCase() === this.taskAction?.taskTypeSubTaskId.toLowerCase()
                ) as TaskTypeLink | undefined;
                if (this.task && this.taskTypeAction?.$type?.toLowerCase() === 'link') {
                  this.taskService.getTaskNotes(taskId).subscribe({
                    next: (resp) => {
                      const apiNotes = resp?.body ?? [];
                      this.recentTaskNote = apiNotes.find((note) => note.taskNoteType === 1)?.comments || '';
                      this.loadingSpinnerService.hideSpinner();
                    },
                    error: (err) => {
                      this.loadingSpinnerService.hideSpinner();
                      console.error('Failed to load task notes', err);
                    }
                  });
                } else {
                  this.loadingSpinnerService.hideSpinner();
                  this.toastService.showToast(ToastMessageType.Error, 'This is not a link task template');
                  this.globalErrorHandlerService.handleError({ message: 'This is not a link task template' });
                }
              },
              error: (error) => {
                this.loadingSpinnerService.hideSpinner();
                this.toastService.showToast(ToastMessageType.Error, 'Task link failed.');
                this.globalErrorHandlerService.handleError(error);
              }
            });
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Error, 'Loading Task details failed.');
          this.globalErrorHandlerService.handleError(error);
        }
      });
  }

  public subTaskEditable(): boolean {
    return this.taskAction?.taskStatusId === 1 || this.taskAction?.taskStatusId === 4;
  }

  public onLinkAccessed(): void {
    if (this.subTaskEditable()) {
      this.linkAccessed = true;
    }
  }

  public onLinkClick(event: MouseEvent): void {
    const url = this.resolvedPageUrl;

    if (/^https?:\/\//i.test(url)) {
      event.preventDefault();
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    this.onLinkAccessed();
  }

  // need the resolver to ensure the url is valid and won't open to localhost
  public resolvePageUrl(url: string | null | undefined): string {
    const trimmed = (url ?? '').trim();
    if (!trimmed || /^(https?:\/\/|\/|mailto:)/i.test(trimmed)) {
      return trimmed;
    }
    return trimmed.startsWith('//') ? `https:${trimmed}` : `https://${trimmed}`;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }

  public onSubmit(): void {
    if (!this.canSubmit || !this.task || !this.taskAction) {
      return;
    }

    this.taskAction.taskStatusId = 2; // Under Review

    this.loadingSpinnerService.showSpinner();
    this.taskService.updateTask(this.task).then(
      () => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Success, 'Task updated successfully.');
        this.onBack();
      },
      (err) => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Error, 'Error updating task status.');
        console.error('error updating task', err);
      }
    );
  }
}
